import {
  Component,
  OnInit,
  Output,
  Input,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormArray,
  AbstractControl,
  UntypedFormControl,
} from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil, catchError, debounceTime, skip } from 'rxjs/operators';

// CONFIGS
import {
  IClientCreditMemo,
  IClientPurchaseOrderDiscount,
  ICompany,
} from '../../../client/interfaces';
import { ECustomFieldObject } from '../../../custom-field';
import { ESalesOrderStatus } from '../../../order';
import { PackageFormComponent } from '../../../package/components/package-form/package-form.component';
import { IPackage } from '../../../package/interfaces/package.interface';
import { IPricingLevel } from '../../../pricing/interfaces/pricing.interface';
import { RfqQuantityFormComponent } from '../../../rfq/components/rfq-quantity-form/rfq-quantity-form.component';
import { RfqUomFormComponent } from '../../../rfq/components/rfq-uom-form/rfq-uom-form.component';
import { RfqTableColumnConfig } from '../../../rfq/config/rfq.config';
import { DialogConfig, SnackBarConfig } from '../../../shared/configs';
import { LocalStorageKeys } from '../../../shared/interfaces/localstorage.interface';
import { IColumnConfig } from '../../../shared/interfaces/table.interface';

// COMPONENTS
import { PackageApiService } from '../../../shared/services/api/package/package-api.service';
import { RfqApiService } from '../../../shared/services/api/rfq/rfq-api.service';
import { trackById } from '../../../shared/util';
import {
  IInvoice,
  EWarehouseDefaultGroup,
  EInvoiceStatus,
} from '../../interfaces/invoice.interface';
import { InvoicePackageDropdownComponent } from '../invoice-package-dropdown/invoice-package-dropdown.component';
import { InvoiceSkuDiscountAmountFormComponent } from '../invoice-sku-discount-amount-form/invoice-sku-discount-amount-form.component';
import { InvoiceSkuDiscountPercentFormComponent } from '../invoice-sku-discount-percent-form/invoice-sku-discount-percent-form.component';
import { InvoiceSkuPriceFormComponent } from '../invoice-sku-price-form/invoice-sku-price-form.component';

import {
  RfqFormComponent,
  RfqLineItemsFormComponent,
  RfqVariantFormComponent,
} from './../../../rfq/components';

// INTERFACES

// SERVICES

import { ClientApiService } from './../../../shared/services/api/client/client-api.service';

// UTILS

@Component({
  selector: 'lum-invoice-skus',
  templateUrl: './invoice-skus.component.html',
  styleUrls: ['./invoice-skus.component.scss'],
})
export class InvoiceSkusComponent implements OnInit {
  @Input()
  packageManagementEnabled :boolean = false;

  @Input()
  newKitsStructureEnabled :boolean = false;

  @Input()
  lotTrackingEnabled :boolean = false;

  @Input()
  customFieldsEnabled :boolean = false;

  @Input()
  discountEntries :IClientPurchaseOrderDiscount[] = [];

  @Input()
  creditMemos :IClientCreditMemo[] = [];

  @Input()
  currentInvoice :IInvoice;

  @Input()
  discountAmount :number = 0;

  @Input()
  templateId :string;

  @Input()
  form :UntypedFormGroup;

  @Input()
  currentCompany :ICompany | null;

  @Input()
  pricingLevels :IPricingLevel[] | null = [];

  @Input()
  permissions :any = {};

  @Input()
  includeAllWarehouseGroups :boolean = false;

  @Output()
  removeLineItem = new EventEmitter<number>();

  @Output()
  removeCreditMemoEntry = new EventEmitter<number>();

  @Output()
  addLineItem = new EventEmitter<{
    item :any; // TODO: Create interface
    index ?:number;
    insertPosition ?:'top' | 'bottom';
  }>();

  @Output()
  addRowItems = new EventEmitter<number>();

  @Output()
  addRowItemSubTotal = new EventEmitter<number | null>();

  @Output()
  addInvoiceCreditMemoEntry = new EventEmitter<UntypedFormGroup>();

  @Output()
  selectRfq = new EventEmitter<{
    item :any; // TODO: Create interface
    tempId :number;
  }>();

  @Output()
  selectCreditMemo = new EventEmitter<{
    creditMemo :IClientCreditMemo;
    index :number;
  }>();

  @Output()
  enterRfqEvent = new EventEmitter<{
    item :any; // TODO: Create interface
    index :number;
    searchStr :string;
  }>();

  @ViewChild(InvoicePackageDropdownComponent)
  packageDropdown :InvoicePackageDropdownComponent;

  @Output()
  recalculateSubtotals = new EventEmitter();

  @ViewChild('fileInput')
  fileInput :ElementRef;

  loading$ :Observable<boolean>;
  rfqs$ :Observable<any[]>; // TODO: Create type
  rfqs :any[] = [];
  searchableFields :string[] = ['sku', 'product_name'];
  searchStr :string = '';
  inventorySettings :any = {};
  scanSearchStr :UntypedFormControl = new UntypedFormControl(null);
  fromScanMode :boolean = false;
  fromEnterEvent :boolean = false; // Capture enter key event
  rfqsBySku :any = {};
  relation :string = [
    'category',
    'subcategory',
    'basePrice',
    'tags.tag_details',
    'images',
    'locationStock',
    'variantParent.category',
    'variantParent.subcategory',
    'variantParent.tags.tag_details',
    'variantParent.unitOfMeasures',
    'rfqAttachments',
    'unitOfMeasures',
    'quotations',
  ].join(',');
  rfqSearchParams :{ sku :string; offset :number; per_page :number } = {
    sku: '',
    offset: 0,
    per_page: 50,
  };
  loadInitialRfq :boolean = false;
  fromPagination :boolean = false;
  rfqTableColumns :IColumnConfig[] = RfqTableColumnConfig;

  trackById = trackById;

  private searchUpdated$ = new Subject<string>();
  private scanSearchUpdated$ = new Subject<string>();
  private onDestroy$ = new Subject();

  constructor(
    private cd :ChangeDetectorRef,
    private dialog :MatDialog,
    private rfqApiService :RfqApiService,
    private clientApiService :ClientApiService,
    private snackBar :MatSnackBar,
    private packageApiService :PackageApiService,
  ) {}

  ngOnInit() {
    this.getFromService();
    this.onSearchUpdate().subscribe();
    this.onScanSearchUpdate().subscribe();
    this.getRfqs().subscribe();
    this.getRfqsLoadingFromObservable().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSearchInputChange(event :any) :void {
    this.searchUpdated$.next(event.target.value);
  }

  onSearchRfqs(sku :any, scanMode :boolean) :void {
    this.rfqs = [];
    this.loadInitialRfq = true;
    this.fromPagination = false;
    this.fromScanMode = scanMode;
    this.fromEnterEvent = false;
    this.rfqSearchParams = {
      sku,
      offset: 0,
      per_page: this.rfqSearchParams.per_page,
    };
    this.scanSearchUpdated$.next(sku);
  }

  onPaginateRfqs() :void {
    this.loadInitialRfq = false;
    this.fromPagination = true;
    this.rfqSearchParams.offset += this.rfqSearchParams.per_page;
    this.scanSearchUpdated$.next(this.rfqSearchParams.sku || '');
  }

  onOpenLineItemsDialog() :void {
    this.dialog
      .open(RfqLineItemsFormComponent, {
        data: {
          warehouse_group_id: this.form.get('warehouse_group_id')?.value,
          client_id: this.form.get('client_id')?.value,
        },
        width: DialogConfig.sizes['xxx-large'],
        height: 'calc(100vh - 150px)',
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((lineItems) => {
        if (lineItems) {
          this.onAddLineItems({ lineItems });
        }
      });
  }

  onOpenSkuDialog() :void {
    const dialogRef = this.dialog.open(RfqFormComponent, {
      data: {
        featureFlag: {
          newKitsStructureEnabled: this.newKitsStructureEnabled,
          lotTrackingEnabled: this.lotTrackingEnabled,
          customFieldsEnabled: this.customFieldsEnabled,
        },
        permissions: this.permissions,
      },
      width: DialogConfig.sizes['xx-large'],
      height: 'calc(100vh - 150px)',
      disableClose: true,
    });

    dialogRef.afterOpened().subscribe(() => {
      import('../../../custom-field').then(({ CustomFieldFormGroupComponent }) => {
        const componentRef =
          dialogRef.componentInstance.customFieldContainerRef.createComponent(
            CustomFieldFormGroupComponent,
          );
        const componentInstance = componentRef.instance;
        componentInstance.customFieldValues = [];
        componentInstance.onChange
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((values) => {
            dialogRef.componentInstance.onCustomFieldChange(values);
          });
        componentInstance.customFieldObject = ECustomFieldObject.RFQ;
      });
    });

    dialogRef.afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe();
  }

  onOpenVariantSkuDialog() :void {
    this.dialog
      .open(RfqVariantFormComponent, {
        data: {
          featureFlag: {
            newKitsStructureEnabled: this.newKitsStructureEnabled,
            lotTrackingEnabled: this.lotTrackingEnabled,
          },
        },
        width: DialogConfig.sizes.medium,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  onRemoveLineItem(index :number) :void {
    this.removeLineItem.emit(index);
  }

  // TODO: Add type lineItems
  onAddLineItems(data :{
    lineItems :any[];
    index ?:number;
    insertPosition ?:'top' | 'bottom';
  }) :void {
    const { lineItems, index, insertPosition = 'bottom' } = data;
    lineItems.forEach((item :any) => {
      this.addLineItem.emit({ item, index, insertPosition });
    });
    this.cd.detectChanges();
  }

  onOverrideChange(override :boolean) :void {
    this.form.patchValue({
      overridden: override ? 1 : 0,
    });
    if (!override) {
      this.lineItemsFormArray.controls.map((control :AbstractControl) => {
        control.patchValue({
          unit_price: control.value.original_unit_price,
          discount_amount: 0,
          discount_percent: 0,
        });
      });
    }
  }

  onFileSelected(event :any) :void {
    try {
      const file :File = event.target.files[0];

      if (file && file.type === 'text/csv') {
        const formData = new FormData();
        formData.append('file', file, file.name);

        // Add warehouse group ID from the form to the FormData
        const warehouseGroupId = this.form.get('warehouse_group_id')?.value;
        if (warehouseGroupId) {
          formData.append('warehouse_group_id', warehouseGroupId.toString());
        }

        this.clientApiService
          .upload(formData)
          .pipe(
            catchError((error) => {
              throw new Error(error);
            }),
            takeUntil(this.onDestroy$),
          )
          .subscribe((data) => {
            const dataHasOverride = data.filter(
              (item :any) => item?.is_overridden,
            )?.length;

            if (dataHasOverride) {
              this.form.patchValue({
                overridden: 1,
              });
            }

            this.onAddLineItems({ lineItems: data });
            this.fileInput.nativeElement.value = '';
          });
      } else {
        throw new Error('Invalid file type or no file selected');
      }
    } catch (error :any) {
      this.fileInput.nativeElement.value = '';
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
    }
  }

  onBulkUpdateQuantity() :void {
    this.dialog
      .open(RfqQuantityFormComponent, {
        data: {},
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((formValue) => {
        if (formValue?.quantity) {
          this.lineItemsFormArray.controls
            .filter(
              (control :AbstractControl) =>
                control.get('selected')?.value && control.get('sku')?.value,
            )
            .map((control :AbstractControl) => {
              control.patchValue({
                quantity: formValue.quantity,
              });
              control.get('quantity')?.markAsDirty();
            });
        }
      });
  }

  onBulkUpdateUOM() :void {
    this.dialog
      .open(RfqUomFormComponent, {
        data: {
          lineItems: this.selectItemsValue,
        },
        width: DialogConfig.sizes.medium,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((formValue) => {
        if (formValue?.unitClassId && formValue?.uomId) {
          this.lineItemsFormArray.controls
            .filter(
              (control :AbstractControl) =>
                control.get('selected')?.value && control.get('sku')?.value,
            )
            .map((control :AbstractControl) => {
              control.patchValue({
                ordered_uom_id: formValue?.uomId,
              });
              control.get('ordered_uom_id')?.markAsDirty();
            });
        }
      });
  }

  onBulkUpdatePrice() :void {
    this.dialog
      .open(InvoiceSkuPriceFormComponent, {
        data: {},
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((formValue) => {
        if (formValue?.unitPrice) {
          this.lineItemsFormArray.controls
            .filter(
              (control :AbstractControl) =>
                control.get('selected')?.value && control.get('sku')?.value,
            )
            .map((control :AbstractControl) => {
              control.patchValue({
                unit_price: formValue.unitPrice,
              });
              control.get('unit_price')?.markAsDirty();
              this.cd.detectChanges();
            });
        }
      });
  }

  onBulkUpdateDiscountAmount() :void {
    this.dialog
      .open(InvoiceSkuDiscountAmountFormComponent, {
        data: {},
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((formValue) => {
        if (formValue?.discountAmount) {
          this.lineItemsFormArray.controls
            .filter(
              (control :AbstractControl) =>
                control.get('selected')?.value && control.get('sku')?.value,
            )
            .map((control :AbstractControl) => {
              control.patchValue({
                discount_amount: formValue.discountAmount,
              });
              control.get('discount_amount')?.markAsDirty();
              this.cd.detectChanges();
            });
        }
      });
  }

  onBulkUpdateDiscountPercent() :void {
    this.dialog
      .open(InvoiceSkuDiscountPercentFormComponent, {
        data: {},
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((formValue) => {
        if (formValue?.discountPercent) {
          this.lineItemsFormArray.controls
            .filter(
              (control :AbstractControl) =>
                control.get('selected')?.value && control.get('sku')?.value,
            )
            .map((control :AbstractControl) => {
              control.patchValue({
                discount_percent: formValue.discountPercent,
              });
              control.get('discount_percent')?.markAsDirty();
            });
        }
      });
  }

  onSelectSku(item :any) :void {
    this.onAddLineItems({
      lineItems: [
        {
          ...item,
        },
      ],
    });
    this.scanSearchStr.setValue(null);
    this.rfqs = [];
  }

  onAddRowItems(count :number) :void {
    this.addRowItems.emit(count);
  }
  onAddInvoiceCreditMemoEntry(invoiceCreditMemoEntry :UntypedFormGroup) :void {
    this.addInvoiceCreditMemoEntry.emit(invoiceCreditMemoEntry);
  }

  onEnterRfqEvent(data :{
    item :AbstractControl; // TODO: Create interface
    index :number;
    searchStr :string;
  }) :void {
    const { item, index, searchStr } = data;
    if (searchStr) {
      this.rfqsBySku[searchStr.toLowerCase()] = { item, index };
      this.fromEnterEvent = true;
      this.fromPagination = false;
    }
  }

  onToggleColumn(event :any, config :IColumnConfig) :void {
    this.rfqTableColumns = this.displayColumns;
    const index = this.rfqTableColumns.findIndex((row) => row.id === config.id);
    this.rfqTableColumns[index].visible = !config.visible;

    localStorage.setItem(
      LocalStorageKeys.INVOICING_FORM_COLUMN_STATE,
      JSON.stringify(this.rfqTableColumns),
    );

    event.stopPropagation();
  }

  onApplyPackage(data :IPackage) :void {
    const rfqIds = data.packageItems.map((item) => item.rfqId);

    this.rfqApiService
      .searchAsync({
        rfqIds: rfqIds.join(', '),
        per_page: '999',
        relations: this.relation,
        includeAllWarehouseGroups: this.includeAllWarehouseGroups ? '1' : '0',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          const updatedLineItems = response.map((lineItem :any) => {
            const correspondingPackageItem = data.packageItems.find(
              (packageItem) => packageItem.rfqId === lineItem.id,
            );

            if (correspondingPackageItem) {
              lineItem.quantity = correspondingPackageItem.quantity;
              lineItem.unit_price = correspondingPackageItem.unitPrice;
              lineItem.isPackageItem = true;
            }

            return lineItem;
          });

          this.onAddLineItems({ lineItems: updatedLineItems });
        },
        error: (error) => {
          this.snackBar.open('Error getting rfqs', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onSavePackage() :void {
    this.dialog
      .open(PackageFormComponent, {
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value) {
          const packageItems = this.lineItemsFormArray.controls
            .filter(
              (item) =>
                item.get('item_id')?.value !== null &&
                item.get('item_id')?.value !== undefined &&
                item.get('product')?.value !== null &&
                item.get('sku')?.value !== '' &&
                item.get('product_name')?.value !== '',
            )
            .map((item) => ({
              rfqId: item.get('item_id')?.value ?? null,
              quantity: item.get('quantity')?.value ?? 0,
              unitPrice: item.get('unitPrice')?.value ?? 0,
            }));

          if (packageItems.length === 0) {
            this.snackBar.open(
              'Error: No items provided. Please add items to create a invoice template.',
              'Fail',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );

            return;
          }

          this.packageApiService
            .create({
              name: value.name,
              packageItems: packageItems,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                if (this.packageDropdown && response) {
                  this.packageDropdown.addPackageToDropdown(response);
                }

                this.snackBar.open(
                  'Invoice Template successfully created.',
                  'Success',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );

                this.cd.detectChanges();
              },
              error: (response) => {
                this.snackBar.open(
                  response?.error || 'Failed to create invoice template',
                  'Fail',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
              },
            });
        }
      });
  }

  private getFromService() :void {
    this.rfqs$ = this.rfqApiService.rfqs$;
    this.loading$ = this.rfqApiService.loading$;
  }

  private getRfqs() :Observable<any[]> {
    return this.rfqs$.pipe(
      skip(1),
      tap((rfqs :any[]) => {
        rfqs.forEach((rfq :any) => {
          this.rfqs.push(rfq);
        });

        if (this.fromPagination) {
          if (rfqs.length === 0) {
            this.snackBar.open('All SKUs successfully loaded.', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        } else {
          if (!rfqs?.length && !this.fromEnterEvent) {
            this.snackBar.open('No SKU not found.', 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });

            this.rfqs = [];
          } else if (this.fromEnterEvent && rfqs?.length) {
            const rfq = rfqs[0];
            const rfqsBySku = this.rfqsBySku[rfq.sku?.toLowerCase()];

            if (rfqsBySku) {
              this.onAddLineItems({ lineItems: [rfq], index: rfqsBySku.index });
              delete this.rfqsBySku[rfq.sku?.toLowerCase()];
            }

            this.rfqs = [];
          } else if (rfqs?.length === 1 && this.fromScanMode) {
            this.onAddLineItems({
              lineItems: [rfqs[0]],
              insertPosition: 'top',
            });
            this.scanSearchStr.setValue(null);
            this.rfqs = [];
          }
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getRfqsLoadingFromObservable() :Observable<boolean> {
    return this.loading$.pipe(
      tap((rfqLoading :boolean) => {
        if (!rfqLoading) {
          this.loadInitialRfq = false;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private onSearchUpdate() :Observable<string> {
    return this.searchUpdated$.asObservable().pipe(
      tap((searchStr :string) => {
        this.searchStr = searchStr;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private onScanSearchUpdate() :Observable<string> {
    return this.scanSearchUpdated$.asObservable().pipe(
      debounceTime(500),
      tap((scanSearchStr :string) => {
        this.rfqApiService.search({
          sku: scanSearchStr,
          relations: this.relation,
          sellable: '1',
          warehouse_group_id:
            this.form.get('warehouse_group_id')?.value ||
            EWarehouseDefaultGroup.DEFAULT,
          per_page: String(this.rfqSearchParams.per_page),
          offset: String(this.rfqSearchParams.offset),
          includeAllWarehouseGroups: this.includeAllWarehouseGroups ? '1' : '0',
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form.get('line_items') as UntypedFormArray;
  }

  get isOverride() :boolean {
    return this.form.get('overridden')?.value || 0;
  }

  get hasSelectedItems() :boolean {
    return this.lineItemsFormArray.value.some(
      (item :any) => item.selected === true && item.sku,
    );
  }

  get selectItemsValue() :any[] {
    // TODO: Add type
    return this.lineItemsFormArray.value.filter(
      (item :any) => item.selected === true,
    );
  }

  get isOverridden() :boolean {
    return this.form.get('overridden')?.value || 0;
  }

  get isSalesOrderShippedStatus() :boolean {
    const salesOrderStatus = this.currentInvoice?.sales_order?.orderStatus || null;

    return salesOrderStatus === ESalesOrderStatus.SHIPPED;
  }

  get displayColumns() :IColumnConfig[] {
    const columnConfigStorage = localStorage.getItem(
      LocalStorageKeys.INVOICING_FORM_COLUMN_STATE,
    );

    if (columnConfigStorage) {
      return JSON.parse(columnConfigStorage) as IColumnConfig[];
    }

    return this.rfqTableColumns;
  }

  get displayColumnCount() :number {
    return this.displayColumns.filter((row) => row.visible).length || 0;
  }

  get isInvoiceStatusComplete() :boolean {
    return this.currentInvoice?.order_status === EInvoiceStatus.COMPLETED;
  }
}
